import { useSearch } from "@tanstack/react-router";
import { useGetConnectionSequenceStepLogQuery } from "api/TenantSlug/ConnectionSequenceStepLogs/hooks.ts";
import { CodeEditor } from "components/UI/CodeEditor";
import { Drawer } from "components/UI/Drawer";
import { DrawerCollapsible } from "components/UI/DrawerCollapsible/DrawerCollapsible.tsx";
import { DrawerContent } from "components/UI/DrawerContent/DrawerContent.tsx";
import { DrawerTitle } from "components/UI/DrawerTitle/DrawerTitle.tsx";
import dayjs from "dayjs";
import { usePaldockTranslation } from "hooks/usePaldockTranslation.tsx";
import { DATE_FORMAT_WITH_TIME } from "localConstants";
import { ConnectionSequenceStepLogModelType } from "types/enums";
import { DrawerId } from "types/models.ts";

interface LogConnectionDrawerDetailProps {
    type: ConnectionSequenceStepLogModelType;
}

export const LogConnectionDrawerDetail = ({ type }: LogConnectionDrawerDetailProps) => {
    const id = DrawerId.ConnectionSequenceStepLog;
    const { t } = usePaldockTranslation("drawers.log-connection-detail");
    const params: {
        connectionSequenceStepLog: string;
    } = useSearch({ strict: false });

    const { data: getConnectionSequenceStepLogQuery, isLoading } = useGetConnectionSequenceStepLogQuery(
        params.connectionSequenceStepLog,
        {
            filter: {
                include:
                    "connectionSequenceStep,offer,conversionState,owner,advertiser,cascade,integration,incomingPostback,outgoingPostback",
            },
        },
        {
            enabled: !!params?.connectionSequenceStepLog,
        }
    );

    if (!params?.connectionSequenceStepLog || !getConnectionSequenceStepLogQuery?.log) {
        return;
    }

    const log = getConnectionSequenceStepLogQuery.log;
    const { request_name, cascade, created_at } = getConnectionSequenceStepLogQuery.log;
    const { title: cascadeTitle } = cascade ?? {};

    let drawerTitle;
    switch (type) {
        case ConnectionSequenceStepLogModelType.Integration:
            drawerTitle = log.integration?.name ?? t("messages.missing_integration_name");
            break;
        case ConnectionSequenceStepLogModelType.OutgoingPostback:
            drawerTitle = log.outgoingPostback?.name ?? t("messages.missing_outgoing_postback_name");
            break;
        case ConnectionSequenceStepLogModelType.IncomingPostback:
            drawerTitle = log.incomingPostback?.name ?? t("messages.missing_incoming_postback_name");
            break;
        default:
            drawerTitle = t("messages.missing_name");
    }

    return (
        <Drawer
            drawerId={id}
            loading={isLoading}
            searchParamsToClean={["connectionSequenceStepLog"]}
            title={
                <DrawerTitle
                    mainTitle={drawerTitle ?? t("messages.missing_name")}
                    subTitles={[request_name, cascadeTitle, dayjs(created_at).format(DATE_FORMAT_WITH_TIME)]}
                />
            }
        >
            {getConnectionSequenceStepLogQuery.log && (
                <DrawerContent>
                    <DrawerCollapsible title={t("sections.request")}>
                        <CodeEditor
                            className="!max-h-80 rounded-lg border"
                            id={`request-detail-${id}`}
                            wrapEnabled
                            minLines={20}
                            maxLines={20}
                        >
                            {getConnectionSequenceStepLogQuery.log.request}
                        </CodeEditor>
                    </DrawerCollapsible>
                    <DrawerCollapsible title={t("sections.response")}>
                        <CodeEditor
                            className="!max-h-80 rounded-lg border"
                            id={`response-detail-${id}`}
                            wrapEnabled
                            minLines={20}
                            maxLines={20}
                        >
                            {getConnectionSequenceStepLogQuery.log.response}
                        </CodeEditor>
                    </DrawerCollapsible>
                    <DrawerCollapsible title={t("sections.debug_logs")}>
                        <CodeEditor
                            className="!max-h-80"
                            id={`debug-logs-detail-${id}`}
                            wrapEnabled
                            minLines={20}
                            maxLines={20}
                        >
                        </CodeEditor>
                    </DrawerCollapsible>
                </DrawerContent>
            )}
        </Drawer>
    );
};
